import type { LidarClearanceLabel } from "../labelsApi";
import type { LidarClearanceAxis } from "../schemas";

// axis_id -> axis_value -> label_name, for labels that curate exactly one
// axis value (see apps/global-api's _single_axis_label_names) - what the
// severity/level dropdowns show instead of a bare number, since the selected
// system's own named labels are the more meaningful way to pick a severity.
export type LabelNamesByAxis = Map<number, Map<number, string>>;

export function labelNamesByAxis(labels: LidarClearanceLabel[]): LabelNamesByAxis {
  const byAxis: LabelNamesByAxis = new Map();
  for (const label of labels) {
    if (!label.label_name || label.axis_values.length !== 1) continue;
    const { axis_id: axisId, axis_value: axisValue } = label.axis_values[0];
    if (!byAxis.has(axisId)) byAxis.set(axisId, new Map());
    byAxis.get(axisId)!.set(axisValue, label.label_name);
  }
  return byAxis;
}

export function axisValueOptionLabel(
  labelNames: LabelNamesByAxis,
  axisId: number,
  value: number
): string {
  const name = labelNames.get(axisId)?.get(value);
  return name ? `${name} (${value})` : String(value);
}

export type AxisValueOption = { value: number; label: string };

// Every valid severity/level for `axis` (1..max_value), labeled via
// axisValueOptionLabel - the option list a severity or risk-tier level
// dropdown offers for that axis.
export function axisValueOptions(
  labelNames: LabelNamesByAxis,
  axis: LidarClearanceAxis
): AxisValueOption[] {
  return Array.from({ length: axis.max_value }, (_, index) => index + 1).map((value) => ({
    value,
    label: axisValueOptionLabel(labelNames, axis.id, value),
  }));
}
